import { useState, useEffect } from "react";
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Checkbox } from "@/components/ui/checkbox";
import { Brain, InfoIcon } from "lucide-react";
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from "@/components/ui/tooltip";
import { Progress } from "@/components/ui/progress";
import { useIsMobile } from "@/hooks/use-mobile";
import { toast } from "sonner";
import { useAnalytics } from "@/hooks/useAnalytics";
import { useTranslations } from "@/hooks/useTranslations";

interface AIWorkoutDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  onGenerate: (params: AIWorkoutParams) => Promise<void>;
  isGenerating: boolean;
  type: string;
  difficulty: string;
  duration: number;
}

export interface AIWorkoutParams {
  type: string;
  difficulty: string;
  duration: number;
  daysCount: number;
  exercisesPerDay: number;
  includeCardio: boolean;
  studentId?: string | null;
}

export function AIWorkoutDialog({
  open,
  onOpenChange,
  onGenerate,
  isGenerating,
  type,
  difficulty,
  duration
}: AIWorkoutDialogProps) {
  const [daysCount, setDaysCount] = useState(1);
  const [exercisesPerDay, setExercisesPerDay] = useState(6);
  const [includeCardio, setIncludeCardio] = useState(false);
  const [isValid, setIsValid] = useState(false);
  const [studentId, setStudentId] = useState<string | null>(null);
  const [progress, setProgress] = useState(0);
  const isMobile = useIsMobile();
  const { sendWorkoutGeneratedEvent } = useAnalytics();
  const { t } = useTranslations("workouts");
  
  useEffect(() => {
    const searchParams = new URLSearchParams(window.location.search);
    const urlStudentId = searchParams.get('studentId');
    if (urlStudentId) {
      setStudentId(urlStudentId);
    }
  }, []);

  useEffect(() => {
    const requiredFieldsValid = Boolean(type && difficulty && duration > 0);
    setIsValid(requiredFieldsValid);
  }, [type, difficulty, duration]);

  useEffect(() => {
    if (!isGenerating) {
      setProgress(0);
      return;
    }
    
    let frameId: number;
    let lastTimestamp = 0;
    const duration = 15000; // 15 segundos para chegar a 95%
    
    const animate = (timestamp: number) => {
      if (!lastTimestamp) lastTimestamp = timestamp;
      const elapsed = timestamp - lastTimestamp;
      
      setProgress(prev => {
        const newProgress = prev + (elapsed / duration) * 95;
        return newProgress >= 95 ? 95 : newProgress;
      });
      
      lastTimestamp = timestamp;
      
      if (isGenerating) {
        frameId = requestAnimationFrame(animate);
      }
    };
    
    frameId = requestAnimationFrame(animate);
    
    return () => {
      cancelAnimationFrame(frameId);
      setProgress(0);
    };
  }, [isGenerating]);

  const handleGenerate = async () => {
    if (!isValid) return;
    
    try {
      // Evento de início de geração de treino
      sendWorkoutGeneratedEvent(type, difficulty, duration);
      
      await onGenerate({
        type,
        difficulty,
        duration,
        daysCount,
        exercisesPerDay,
        includeCardio,
        studentId
      });
    } catch (error) {
      toast.error(t("aiWorkout.generationError"));
      console.error("Erro ao gerar treino:", error);
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className={`${isMobile ? 'w-[90%] max-w-[350px]' : 'sm:max-w-md'} rounded-2xl`}>
        <DialogHeader>
          <DialogTitle>{t("aiWorkout.title")}</DialogTitle>
          <DialogDescription>
            {t("aiWorkout.description")}
          </DialogDescription>
        </DialogHeader>
        <div className="space-y-4 py-4">
          <div className="grid grid-cols-2 gap-4">
            <div className="space-y-2">
              <Label htmlFor="days">{t("aiWorkout.daysCount")}</Label>
              <Input 
                id="days" 
                type="number" 
                min={1} 
                max={7} 
                value={daysCount} 
                onChange={e => setDaysCount(Number(e.target.value))} 
                className="bg-white"
              />
            </div>
            <div className="space-y-2">
              <div className="flex items-center">
                <Label htmlFor="exercises" className="mr-2">{t("aiWorkout.exercisesPerDay")}</Label>
                <TooltipProvider>
                  <Tooltip>
                    <TooltipTrigger>
                      <InfoIcon className="h-4 w-4 text-muted-foreground" />
                    </TooltipTrigger>
                    <TooltipContent>
                      <p className="max-w-xs">
                        {t("aiWorkout.exercisesPerDayTooltip")}
                      </p>
                    </TooltipContent>
                  </Tooltip>
                </TooltipProvider>
              </div>
              <Input 
                id="exercises" 
                type="number" 
                min={3} 
                max={10} 
                value={exercisesPerDay} 
                onChange={e => setExercisesPerDay(Number(e.target.value))} 
                className="bg-white"
              />
            </div>
          </div>
          
          {type === "musculacao" && (
            <div className="flex items-center space-x-2">
              <Checkbox 
                id="cardio" 
                checked={includeCardio} 
                onCheckedChange={checked => setIncludeCardio(checked as boolean)} 
              />
              <Label htmlFor="cardio" className="flex items-center">
                {t("aiWorkout.includeCardio")}
                <TooltipProvider>
                  <Tooltip>
                    <TooltipTrigger>
                      <InfoIcon className="h-4 w-4 ml-2 text-muted-foreground" />
                    </TooltipTrigger>
                    <TooltipContent>
                      <p className="max-w-xs">
                        {t("aiWorkout.cardioTooltip")}
                      </p>
                    </TooltipContent>
                  </Tooltip>
                </TooltipProvider>
              </Label>
            </div>
          )}
          
          {studentId && (
            <div className="p-3 bg-muted rounded-md">
              <p className="text-sm text-muted-foreground">
                {t("aiWorkout.studentProfile")}
              </p>
            </div>
          )}
          
          <Button 
            onClick={handleGenerate} 
            disabled={isGenerating || !isValid} 
            className="w-full"
          >
            <Brain className="mr-2 h-4 w-4" />
            {isGenerating ? t("aiWorkout.generating") : t("aiWorkout.generateButton")}
          </Button>
          
          {isGenerating && (
            <div className="space-y-1">
              <Progress value={progress} className="h-2" />
              <p className="text-xs text-center text-muted-foreground">
                {t("aiWorkout.generating")}
              </p>
            </div>
          )}
          
          {!isValid && (
            <p className="text-sm text-destructive">
              {t("aiWorkout.fillRequired")}
            </p>
          )}
          
          <p className="text-xs text-center text-muted-foreground mt-2">
            {t("aiWorkout.aiDisclaimer")}
          </p>
        </div>
      </DialogContent>
    </Dialog>
  );
}
